<template>
  <div class="flex flex-wrap">
    <div class="w-full px-4">
      <!-- Hero -->
      <div class="rounded-2xl shadow-xl bg-white overflow-hidden mb-8 border border-gray-100">
        <div class="bg-respina-primary px-6 py-8 text-white">
          <h1 class="text-2xl md:text-3xl font-bold mb-1">Configuration</h1>
          <p class="text-respina-light text-sm opacity-90">
            Manage app settings and versions
          </p>
        </div>
      </div>

      <!-- Error -->
      <div
        v-if="error"
        class="mb-6 rounded-xl bg-red-50 border border-red-200 text-red-700 px-6 py-4 flex items-center justify-between"
      >
        <span><i class="fas fa-exclamation-circle ml-2"></i>{{ error }}</span>
        <button type="button" class="text-red-500 hover:text-red-700" @click="error = null">×</button>
      </div>

      <!-- Success -->
      <div
        v-if="saved"
        class="mb-6 rounded-xl bg-green-50 border border-green-200 text-green-700 px-6 py-4 flex items-center justify-between"
      >
        <span><i class="fas fa-check-circle ml-2"></i>Settings saved successfully.</span>
        <button type="button" class="text-green-600 hover:text-green-800" @click="saved = false">×</button>
      </div>

      <!-- Form -->
      <div class="rounded-2xl shadow-lg bg-white overflow-hidden border border-gray-100">
        <div v-if="loading" class="py-16 text-center text-gray-500">
          <i class="fas fa-spinner fa-spin text-2xl mb-3"></i>
          <p>Loading...</p>
        </div>
        <form v-else @submit.prevent="save" class="p-6 md:p-8">
          <!-- Base URL -->
          <div class="mb-8">
            <label class="flex items-center gap-2 text-gray-700 text-sm font-semibold mb-2">
              <i class="fas fa-link text-respina-primary"></i>
              Base URL
            </label>
            <input
              v-model="form.baseUrl"
              type="text"
              class="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-respina-primary focus:ring-2 focus:ring-blue-200 outline-none transition-all"
              placeholder="https://api.example.com"
            />
          </div>

          <!-- Show Captcha -->
          <div class="mb-8">
            <label
              class="flex items-center gap-3 cursor-pointer p-4 rounded-xl border-2 border-gray-200 hover:border-respina-accent transition-colors"
              :class="{ 'border-respina-primary bg-respina-light': form.showCaptcha }"
            >
              <input
                v-model="form.showCaptcha"
                type="checkbox"
                class="h-5 w-5 rounded border-gray-300 text-respina-primary focus:ring-respina-primary"
              />
              <i class="fas fa-shield-alt text-respina-primary"></i>
              <span class="text-gray-700 font-medium">Show Captcha</span>
            </label>
          </div>

          <!-- Show Last-Minute Tour Offers -->
          <div class="mb-8">
            <label
              class="flex items-center gap-3 cursor-pointer p-4 rounded-xl border-2 border-gray-200 hover:border-respina-accent transition-colors"
              :class="{ 'border-respina-primary bg-respina-light': form.showLastMinuteTourOffers }"
            >
              <input
                v-model="form.showLastMinuteTourOffers"
                type="checkbox"
                class="h-5 w-5 rounded border-gray-300 text-respina-primary focus:ring-respina-primary"
              />
              <i class="fas fa-clock text-respina-primary"></i>
              <span class="text-gray-700 font-medium">Show Last-Minute Tour Offers</span>
            </label>
          </div>

          <!-- Show Contact Me -->
          <div class="mb-8">
            <label
              class="flex items-center gap-3 cursor-pointer p-4 rounded-xl border-2 border-gray-200 hover:border-respina-accent transition-colors"
              :class="{ 'border-respina-primary bg-respina-light': form.showContactMe }"
            >
              <input
                v-model="form.showContactMe"
                type="checkbox"
                class="h-5 w-5 rounded border-gray-300 text-respina-primary focus:ring-respina-primary"
              />
              <i class="fas fa-phone text-respina-primary"></i>
              <span class="text-gray-700 font-medium">Show Contact Me</span>
            </label>
          </div>

          <!-- App Versions -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div>
              <label class="flex items-center gap-2 text-gray-700 text-sm font-semibold mb-2">
                <i class="fab fa-apple text-gray-700"></i>
                App Version (iOS)
              </label>
              <input
                v-model="form.appVersionIos"
                type="text"
                class="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-respina-primary focus:ring-2 focus:ring-blue-200 outline-none transition-all"
                placeholder="1.0.0"
              />
            </div>
            <div>
              <label class="flex items-center gap-2 text-gray-700 text-sm font-semibold mb-2">
                <i class="fab fa-android text-green-600"></i>
                App Version (Android)
              </label>
              <input
                v-model="form.appVersionAndroid"
                type="text"
                class="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-respina-primary focus:ring-2 focus:ring-blue-200 outline-none transition-all"
                placeholder="1.0.0"
              />
            </div>
          </div>

          <!-- Feature Visibility Toggles -->
          <div class="mb-8">
            <h3 class="flex items-center gap-2 text-gray-700 text-sm font-semibold mb-4">
              <i class="fas fa-eye text-respina-primary"></i>
              Feature Visibility
            </h3>
            <div class="grid grid-cols-1 gap-8">

              <!-- ====== FLIGHT GROUP ====== -->
              <div class="rounded-xl border-2 overflow-hidden transition-colors"
                :class="form.configs.flgt.showPg ? 'border-respina-primary bg-respina-light' : 'border-red-300 bg-red-50/30'">
                <label class="flex items-center gap-3 cursor-pointer p-4 transition-colors">
                  <input v-model="form.configs.flgt.showPg" type="checkbox"
                    class="h-5 w-5 rounded border-gray-300 text-respina-primary focus:ring-respina-primary"
                    @change="onGroupToggle('flgt')" />
                  <i class="fas fa-plane text-respina-primary"></i>
                  <span class="text-gray-700 font-medium">Flights</span>
                </label>
                <!-- Search toggle -->
                <div class="px-4 pb-2 border-t border-gray-100">
                  <label class="flex items-center gap-2 cursor-pointer text-sm text-gray-600 py-1">
                    <input v-model="form.configs.flgt.search" type="checkbox"
                      class="h-4 w-4 rounded border-gray-300 text-respina-primary focus:ring-respina-primary" />
                    <i class="fas fa-search text-gray-400 text-xs"></i>
                    <span>Search</span>
                  </label>
                </div>
                <!-- If OFF: show group link -->
                <div v-if="!form.configs.flgt.showPg" class="px-4 pb-4">
                  <label class="flex items-center gap-2 text-gray-500 text-xs font-semibold mb-1">
                    <i class="fas fa-external-link-alt"></i> Redirect Link
                  </label>
                  <input v-model="form.configs.flgt.lnk" type="text"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-respina-primary focus:ring-1 focus:ring-blue-200 outline-none transition-all text-sm"
                    placeholder="https://..." />
                </div>
                <!-- If ON: show sub-items -->
                <div v-if="form.configs.flgt.showPg" class="border-t border-gray-200 ml-8 mr-4 mb-4 pt-3 space-y-3">
                  <!-- Domestic Flight -->
                  <div class="rounded-lg border overflow-hidden transition-colors"
                    :class="form.configs.flgt.showDmstkFlgt ? 'border-blue-200 bg-blue-50/40' : 'border-red-200 bg-red-50/30'">
                    <label class="flex items-center gap-3 cursor-pointer p-3 text-sm">
                      <input v-model="form.configs.flgt.showDmstkFlgt" type="checkbox"
                        class="h-4 w-4 rounded border-gray-300 text-respina-primary focus:ring-respina-primary"
                        @change="onSubToggle('flgt', 'showDmstkFlgt', 'dmstkFlgtLnk')" />
                      <i class="fas fa-plane-departure text-blue-500"></i>
                      <span class="text-gray-600 font-medium">Domestic Flights</span>
                    </label>
                    <div v-if="!form.configs.flgt.showDmstkFlgt" class="px-3 pb-3">
                      <input v-model="form.configs.flgt.dmstkFlgtLnk" type="text"
                        class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="https://..." />
                    </div>
                  </div>
                  <!-- International Flight -->
                  <div class="rounded-lg border overflow-hidden transition-colors"
                    :class="form.configs.flgt.showIntFlgt ? 'border-blue-200 bg-blue-50/40' : 'border-red-200 bg-red-50/30'">
                    <label class="flex items-center gap-3 cursor-pointer p-3 text-sm">
                      <input v-model="form.configs.flgt.showIntFlgt" type="checkbox"
                        class="h-4 w-4 rounded border-gray-300 text-respina-primary focus:ring-respina-primary"
                        @change="onSubToggle('flgt', 'showIntFlgt', 'intFlgtLnk')" />
                      <i class="fas fa-globe text-blue-500"></i>
                      <span class="text-gray-600 font-medium">International Flights</span>
                    </label>
                    <div v-if="!form.configs.flgt.showIntFlgt" class="px-3 pb-3">
                      <input v-model="form.configs.flgt.intFlgtLnk" type="text"
                        class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="https://..." />
                    </div>
                  </div>
                </div>
              </div>

              <!-- ====== TRAIN (standalone) ====== -->
              <div class="rounded-xl border-2 overflow-hidden transition-colors"
                :class="form.configs.showTrin.showPg ? 'border-respina-primary bg-respina-light' : 'border-red-300 bg-red-50/30'">
                <label class="flex items-center gap-3 cursor-pointer p-4 transition-colors">
                  <input v-model="form.configs.showTrin.showPg" type="checkbox"
                    class="h-5 w-5 rounded border-gray-300 text-respina-primary focus:ring-respina-primary"
                    @change="onSimpleToggle('showTrin')" />
                  <i class="fas fa-train text-respina-primary"></i>
                  <span class="text-gray-700 font-medium">Train</span>
                </label>
                <div class="px-4 pb-1 border-t border-gray-100">
                  <label class="flex items-center gap-2 cursor-pointer text-sm text-gray-600 py-1">
                    <input v-model="form.configs.showTrin.search" type="checkbox"
                      class="h-4 w-4 rounded border-gray-300 text-respina-primary focus:ring-respina-primary" />
                    <i class="fas fa-search text-gray-400 text-xs"></i>
                    <span>Search</span>
                  </label>
                </div>
                <div class="px-4 pb-4">
                  <label class="flex items-center gap-2 text-gray-500 text-xs font-semibold mb-1">
                    <i class="fas fa-external-link-alt"></i> Redirect Link
                  </label>
                  <input v-model="form.configs.showTrin.lnk" type="text"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-respina-primary focus:ring-1 focus:ring-blue-200 outline-none transition-all text-sm"
                    placeholder="https://..." />
                </div>
              </div>

              <!-- ====== BUS (standalone) ====== -->
              <div class="rounded-xl border-2 overflow-hidden transition-colors"
                :class="form.configs.showBs.showPg ? 'border-respina-primary bg-respina-light' : 'border-red-300 bg-red-50/30'">
                <label class="flex items-center gap-3 cursor-pointer p-4 transition-colors">
                  <input v-model="form.configs.showBs.showPg" type="checkbox"
                    class="h-5 w-5 rounded border-gray-300 text-respina-primary focus:ring-respina-primary"
                    @change="onSimpleToggle('showBs')" />
                  <i class="fas fa-bus text-respina-primary"></i>
                  <span class="text-gray-700 font-medium">Bus</span>
                </label>
                <div class="px-4 pb-1 border-t border-gray-100">
                  <label class="flex items-center gap-2 cursor-pointer text-sm text-gray-600 py-1">
                    <input v-model="form.configs.showBs.search" type="checkbox"
                      class="h-4 w-4 rounded border-gray-300 text-respina-primary focus:ring-respina-primary" />
                    <i class="fas fa-search text-gray-400 text-xs"></i>
                    <span>Search</span>
                  </label>
                </div>
                <div class="px-4 pb-4">
                  <label class="flex items-center gap-2 text-gray-500 text-xs font-semibold mb-1">
                    <i class="fas fa-external-link-alt"></i> Redirect Link
                  </label>
                  <input v-model="form.configs.showBs.lnk" type="text"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-respina-primary focus:ring-1 focus:ring-blue-200 outline-none transition-all text-sm"
                    placeholder="https://..." />
                </div>
              </div>

              <!-- ====== TOUR GROUP ====== -->
              <div class="rounded-xl border-2 overflow-hidden transition-colors"
                :class="form.configs.tur.showPg ? 'border-respina-primary bg-respina-light' : 'border-red-300 bg-red-50/30'">
                <label class="flex items-center gap-3 cursor-pointer p-4 transition-colors">
                  <input v-model="form.configs.tur.showPg" type="checkbox"
                    class="h-5 w-5 rounded border-gray-300 text-respina-primary focus:ring-respina-primary"
                    @change="onGroupToggle('tur')" />
                  <i class="fas fa-umbrella-beach text-respina-primary"></i>
                  <span class="text-gray-700 font-medium">Tours</span>
                </label>
                <!-- Search toggle -->
                <div class="px-4 pb-2 border-t border-gray-100">
                  <label class="flex items-center gap-2 cursor-pointer text-sm text-gray-600 py-1">
                    <input v-model="form.configs.tur.search" type="checkbox"
                      class="h-4 w-4 rounded border-gray-300 text-respina-primary focus:ring-respina-primary" />
                    <i class="fas fa-search text-gray-400 text-xs"></i>
                    <span>Search</span>
                  </label>
                </div>
                <div v-if="!form.configs.tur.showPg" class="px-4 pb-4">
                  <label class="flex items-center gap-2 text-gray-500 text-xs font-semibold mb-1">
                    <i class="fas fa-external-link-alt"></i> Redirect Link
                  </label>
                  <input v-model="form.configs.tur.lnk" type="text"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-respina-primary focus:ring-1 focus:ring-blue-200 outline-none transition-all text-sm"
                    placeholder="https://..." />
                </div>
                <div v-if="form.configs.tur.showPg" class="border-t border-gray-200 ml-8 mr-4 mb-4 pt-3 space-y-3">
                  <!-- Domestic Tour -->
                  <div class="rounded-lg border overflow-hidden transition-colors"
                    :class="form.configs.tur.showDmstkTur ? 'border-blue-200 bg-blue-50/40' : 'border-red-200 bg-red-50/30'">
                    <label class="flex items-center gap-3 cursor-pointer p-3 text-sm">
                      <input v-model="form.configs.tur.showDmstkTur" type="checkbox"
                        class="h-4 w-4 rounded border-gray-300 text-respina-primary focus:ring-respina-primary"
                        @change="onSubToggle('tur', 'showDmstkTur', 'dmstkTurLnk')" />
                      <i class="fas fa-mountain text-blue-500"></i>
                      <span class="text-gray-600 font-medium">Domestic Tour</span>
                    </label>
                    <div v-if="!form.configs.tur.showDmstkTur" class="px-3 pb-3">
                      <input v-model="form.configs.tur.dmstkTurLnk" type="text"
                        class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="https://..." />
                    </div>
                  </div>
                  <!-- International Tour -->
                  <div class="rounded-lg border overflow-hidden transition-colors"
                    :class="form.configs.tur.showIntTur ? 'border-blue-200 bg-blue-50/40' : 'border-red-200 bg-red-50/30'">
                    <label class="flex items-center gap-3 cursor-pointer p-3 text-sm">
                      <input v-model="form.configs.tur.showIntTur" type="checkbox"
                        class="h-4 w-4 rounded border-gray-300 text-respina-primary focus:ring-respina-primary"
                        @change="onSubToggle('tur', 'showIntTur', 'intTurLnk')" />
                      <i class="fas fa-globe-americas text-blue-500"></i>
                      <span class="text-gray-600 font-medium">International Tour</span>
                    </label>
                    <div v-if="!form.configs.tur.showIntTur" class="px-3 pb-3">
                      <input v-model="form.configs.tur.intTurLnk" type="text"
                        class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="https://..." />
                    </div>
                  </div>
                </div>
              </div>

              <!-- ====== GROUP TOUR (standalone) ====== -->
              <div class="rounded-xl border-2 overflow-hidden transition-colors"
                :class="form.configs.showGTrur.showPg ? 'border-respina-primary bg-respina-light' : 'border-red-300 bg-red-50/30'">
                <label class="flex items-center gap-3 cursor-pointer p-4 transition-colors">
                  <input v-model="form.configs.showGTrur.showPg" type="checkbox"
                    class="h-5 w-5 rounded border-gray-300 text-respina-primary focus:ring-respina-primary"
                    @change="onSimpleToggle('showGTrur')" />
                  <i class="fas fa-users text-respina-primary"></i>
                  <span class="text-gray-700 font-medium">Group Tour</span>
                </label>
                <div class="px-4 pb-1 border-t border-gray-100">
                  <label class="flex items-center gap-2 cursor-pointer text-sm text-gray-600 py-1">
                    <input v-model="form.configs.showGTrur.search" type="checkbox"
                      class="h-4 w-4 rounded border-gray-300 text-respina-primary focus:ring-respina-primary" />
                    <i class="fas fa-search text-gray-400 text-xs"></i>
                    <span>Search</span>
                  </label>
                </div>
                <div class="px-4 pb-4">
                  <label class="flex items-center gap-2 text-gray-500 text-xs font-semibold mb-1">
                    <i class="fas fa-external-link-alt"></i> Redirect Link
                  </label>
                  <input v-model="form.configs.showGTrur.lnk" type="text"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-respina-primary focus:ring-1 focus:ring-blue-200 outline-none transition-all text-sm"
                    placeholder="https://..." />
                </div>
              </div>

              <!-- ====== HOTEL GROUP ====== -->
              <div class="rounded-xl border-2 overflow-hidden transition-colors"
                :class="form.configs.htl.showPg ? 'border-respina-primary bg-respina-light' : 'border-red-300 bg-red-50/30'">
                <label class="flex items-center gap-3 cursor-pointer p-4 transition-colors">
                  <input v-model="form.configs.htl.showPg" type="checkbox"
                    class="h-5 w-5 rounded border-gray-300 text-respina-primary focus:ring-respina-primary"
                    @change="onGroupToggle('htl')" />
                  <i class="fas fa-hotel text-respina-primary"></i>
                  <span class="text-gray-700 font-medium">Hotels</span>
                </label>
                <!-- Search toggle -->
                <div class="px-4 pb-2 border-t border-gray-100">
                  <label class="flex items-center gap-2 cursor-pointer text-sm text-gray-600 py-1">
                    <input v-model="form.configs.htl.search" type="checkbox"
                      class="h-4 w-4 rounded border-gray-300 text-respina-primary focus:ring-respina-primary" />
                    <i class="fas fa-search text-gray-400 text-xs"></i>
                    <span>Search</span>
                  </label>
                </div>
                <div v-if="!form.configs.htl.showPg" class="px-4 pb-4">
                  <label class="flex items-center gap-2 text-gray-500 text-xs font-semibold mb-1">
                    <i class="fas fa-external-link-alt"></i> Redirect Link
                  </label>
                  <input v-model="form.configs.htl.lnk" type="text"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-respina-primary focus:ring-1 focus:ring-blue-200 outline-none transition-all text-sm"
                    placeholder="https://..." />
                </div>
                <div v-if="form.configs.htl.showPg" class="border-t border-gray-200 ml-8 mr-4 mb-4 pt-3 space-y-3">
                  <!-- Domestic Hotel -->
                  <div class="rounded-lg border overflow-hidden transition-colors"
                    :class="form.configs.htl.showDmstkHtl ? 'border-blue-200 bg-blue-50/40' : 'border-red-200 bg-red-50/30'">
                    <label class="flex items-center gap-3 cursor-pointer p-3 text-sm">
                      <input v-model="form.configs.htl.showDmstkHtl" type="checkbox"
                        class="h-4 w-4 rounded border-gray-300 text-respina-primary focus:ring-respina-primary"
                        @change="onSubToggle('htl', 'showDmstkHtl', 'dmstkHtlLnk')" />
                      <i class="fas fa-bed text-blue-500"></i>
                      <span class="text-gray-600 font-medium">Domestic Hotel</span>
                    </label>
                    <div v-if="!form.configs.htl.showDmstkHtl" class="px-3 pb-3">
                      <input v-model="form.configs.htl.dmstkHtlLnk" type="text"
                        class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="https://..." />
                    </div>
                  </div>
                  <!-- International Hotel -->
                  <div class="rounded-lg border overflow-hidden transition-colors"
                    :class="form.configs.htl.showIntHtl ? 'border-blue-200 bg-blue-50/40' : 'border-red-200 bg-red-50/30'">
                    <label class="flex items-center gap-3 cursor-pointer p-3 text-sm">
                      <input v-model="form.configs.htl.showIntHtl" type="checkbox"
                        class="h-4 w-4 rounded border-gray-300 text-respina-primary focus:ring-respina-primary"
                        @change="onSubToggle('htl', 'showIntHtl', 'intHtlLnk')" />
                      <i class="fas fa-concierge-bell text-blue-500"></i>
                      <span class="text-gray-600 font-medium">International Hotel</span>
                    </label>
                    <div v-if="!form.configs.htl.showIntHtl" class="px-3 pb-3">
                      <input v-model="form.configs.htl.intHtlLnk" type="text"
                        class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="https://..." />
                    </div>
                  </div>
                </div>
              </div>

              <!-- ====== MAIN (standalone) ====== -->
              <div class="rounded-xl border-2 overflow-hidden transition-colors"
                :class="form.configs.showMain ? 'border-respina-primary bg-respina-light' : 'border-red-300 bg-red-50/30'">
                <label class="flex items-center gap-3 cursor-pointer p-4 transition-colors">
                  <input v-model="form.configs.showMain" type="checkbox"
                    class="h-5 w-5 rounded border-gray-300 text-respina-primary focus:ring-respina-primary"
                    @change="onSimpleToggle('showMain', 'mainLnk')" />
                  <i class="fas fa-home text-respina-primary"></i>
                  <span class="text-gray-700 font-medium">Main</span>
                </label>
                <div v-if="!form.configs.showMain" class="px-4 pb-4">
                  <label class="flex items-center gap-2 text-gray-500 text-xs font-semibold mb-1">
                    <i class="fas fa-external-link-alt"></i> Redirect Link
                  </label>
                  <input v-model="form.configs.mainLnk" type="text"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-respina-primary focus:ring-1 focus:ring-blue-200 outline-none transition-all text-sm"
                    placeholder="https://..." />
                </div>
              </div>

            </div>
          </div>

          <div class="pt-4 border-t border-gray-100">
            <button
              type="submit"
              :disabled="saving"
              class="inline-flex items-center justify-center gap-2 min-w-[160px] h-12 px-6 bg-respina-primary hover:bg-respina-secondary text-white font-semibold text-sm rounded-xl shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:shadow-md"
            >
              <i class="fas text-sm" :class="saving ? 'fa-spinner fa-spin' : 'fa-check'"></i>
              <span>{{ saving ? "Saving..." : "Save changes" }}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { apiGet, apiPut } from "@/lib/http";

function defaultConfigs() {
  return {
    flgt: {
      showPg: true, search: false, lnk: "",
      showDmstkFlgt: true, dmstkFlgtLnk: "",
      showIntFlgt: true, intFlgtLnk: "",
    },
    showTrin: { showPg: true, search: false, lnk: "" },
    showBs: { showPg: true, search: false, lnk: "" },
    tur: {
      showPg: true, search: false, lnk: "",
      showDmstkTur: true, dmstkTurLnk: "",
      showIntTur: true, intTurLnk: "",
    },
    showGTrur: { showPg: true, search: false, lnk: "" },
    htl: {
      showPg: true, search: false, lnk: "",
      showDmstkHtl: true, dmstkHtlLnk: "",
      showIntHtl: true, intHtlLnk: "",
    },
    showMain: true, mainLnk: "",
  };
}

const GROUP_KEYS = {
  flgt: {
    subItems: [
      { showKey: "showDmstkFlgt", linkKey: "dmstkFlgtLnk" },
      { showKey: "showIntFlgt", linkKey: "intFlgtLnk" },
    ],
  },
  tur: {
    subItems: [
      { showKey: "showDmstkTur", linkKey: "dmstkTurLnk" },
      { showKey: "showIntTur", linkKey: "intTurLnk" },
    ],
  },
  htl: {
    subItems: [
      { showKey: "showDmstkHtl", linkKey: "dmstkHtlLnk" },
      { showKey: "showIntHtl", linkKey: "intHtlLnk" },
    ],
  },
};

export default {
  data() {
    return {
      form: {
        baseUrl: "",
        showCaptcha: false,
        showLastMinuteTourOffers: false,
        showContactMe: false,
        appVersionIos: "",
        appVersionAndroid: "",
        configs: defaultConfigs(),
      },
      loading: true,
      saving: false,
      saved: false,
      error: null,
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    onGroupToggle(groupKey) {
      const group = this.form.configs[groupKey];
      if (group.showPg) {
        // Turned ON -> clear group link
        group.lnk = "";
      } else {
        // Turned OFF -> clear all sub links
        const info = GROUP_KEYS[groupKey];
        for (const sub of info.subItems) {
          group[sub.linkKey] = "";
        }
      }
    },
    onSubToggle(groupKey, showKey, linkKey) {
      const group = this.form.configs[groupKey];
      if (group[showKey]) {
        group[linkKey] = "";
      }
    },
    onSimpleToggle() {},
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const data = await apiGet("/api/config");
        this.form.baseUrl = data?.baseUrl ?? "";
        this.form.showCaptcha = !!data?.showCaptcha;
        this.form.showLastMinuteTourOffers = !!data?.showLastMinuteTourOffers;
        this.form.showContactMe = !!data?.showContactMe;
        this.form.appVersionIos = data?.appVersionIos ?? "";
        this.form.appVersionAndroid = data?.appVersionAndroid ?? "";

        const c = data?.configs || {};
        const def = defaultConfigs();

        // Merge grouped configs
        const configs = {
          flgt: { ...def.flgt, ...c.flgt },
          showTrin: { ...def.showTrin, ...(c.showTrin && typeof c.showTrin === 'object' ? c.showTrin : {}) },
          showBs: { ...def.showBs, ...(c.showBs && typeof c.showBs === 'object' ? c.showBs : {}) },
          tur: { ...def.tur, ...c.tur },
          showGTrur: { ...def.showGTrur, ...(c.showGTrur && typeof c.showGTrur === 'object' ? c.showGTrur : {}) },
          htl: { ...def.htl, ...c.htl },
          showMain: c.showMain ?? def.showMain,
          mainLnk: c.mainLnk ?? def.mainLnk,
        };
        this.form.configs = configs;
      } catch (e) {
        this.error = e?.message ?? "Failed to load config";
      } finally {
        this.loading = false;
      }
    },
    async save() {
      this.saving = true;
      this.error = null;
      this.saved = false;
      try {
        // Deep clone configs and clear links for enabled items
        const configs = JSON.parse(JSON.stringify(this.form.configs));

        // Groups: if group ON, clear group link; if sub ON, clear sub link
        for (const [gk, info] of Object.entries(GROUP_KEYS)) {
          if (configs[gk].showPg) {
            configs[gk].lnk = "";
            for (const sub of info.subItems) {
              if (configs[gk][sub.showKey]) {
                configs[gk][sub.linkKey] = "";
              }
            }
          } else {
            // Group OFF: clear all sub links (only group link matters)
            for (const sub of info.subItems) {
              configs[gk][sub.linkKey] = "";
            }
          }
        }

        // showMain stays flat
        if (configs.showMain) {
          configs.mainLnk = "";
        }

        await apiPut("/api/config", {
          baseUrl: this.form.baseUrl,
          showCaptcha: this.form.showCaptcha,
          showLastMinuteTourOffers: this.form.showLastMinuteTourOffers,
          showContactMe: this.form.showContactMe,
          appVersionIos: this.form.appVersionIos,
          appVersionAndroid: this.form.appVersionAndroid,
          configs,
        });
        this.saved = true;
      } catch (e) {
        this.error = e?.message ?? "Failed to save config";
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
